import { ConcurrentTenancyCheckResultDTO, ConflictingRepositDTOStatusEnum } from '@reposit/api-client';
import React from 'react';
import styled from 'styled-components';
import { RepositTheme } from '../../../base/theme';
import IdentifierValue from '../../../components/IdentifierValue';
import { P2 } from '../../../components/Typography';
import { analyzeConcurrentTenancy } from './concurrentTenancyUtils';

const CONFLICT_STATUS_LABELS: Record<ConflictingRepositDTOStatusEnum, string> = {
  [ConflictingRepositDTOStatusEnum.PENDING]: 'Awaiting Tenant Actions',
  [ConflictingRepositDTOStatusEnum.COMPLETE]: 'Active',
};

const POPPINS_FONT = '"Poppins", sans-serif';

interface ConcurrentTenancyConflictPanelProps {
  checkResult: ConcurrentTenancyCheckResultDTO | null;
  checkError: string | null;
  checkedEmail: string;
}

const CheckResultPanel = styled.div<{ isWarning?: boolean; isError?: boolean }>`
  margin-top: 20px;
  margin-bottom: 20px;
  padding: 20px;
  border-radius: 8px;
  font-family: ${POPPINS_FONT};
  background-color: ${(props) => {
    if (props.isError) return 'rgba(232, 49, 81, 0.08)';
    if (props.isWarning) return 'rgba(58, 49, 94, 0.06)';
    return 'rgba(64, 201, 162, 0.1)';
  }};
  border: 1px solid ${(props) => {
    if (props.isError) return props.theme.colors.negative;
    if (props.isWarning) return props.theme.colors.primaryBrand;
    return props.theme.colors.positive;
  }};
`;

const ResultMessage = styled(P2)`
  margin: 0 0 16px 0;
  font-weight: 600;
  line-height: 1.5;
  font-family: ${POPPINS_FONT};
  color: ${(props) => props.theme.colors.body};
`;

const ConflictList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ConflictItem = styled.li`
  margin-bottom: 16px;
  padding: 12px 14px;
  background: ${(props) => props.theme.colors.bgAccent};
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.5;
  font-family: ${POPPINS_FONT};
  color: ${(props) => props.theme.colors.body};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ConflictRow = styled.div`
  display: flex;
  margin-bottom: 6px;
  gap: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ConflictLabel = styled.span`
  flex: 0 0 140px;
  color: ${(props) => props.theme.colors.body2};
  font-weight: 500;
  font-family: ${POPPINS_FONT};
`;

const ConflictValue = styled.span`
  flex: 1;
  font-family: ${POPPINS_FONT};
  color: ${(props) => props.theme.colors.body};
`;

export const ConcurrentTenancyConflictPanel: React.FC<ConcurrentTenancyConflictPanelProps> = ({
  checkResult,
  checkError,
  checkedEmail,
}) => {
  const analysis = checkResult && checkedEmail ? analyzeConcurrentTenancy(checkResult, checkedEmail) : null;
  const isBlockedOrError = !!checkError || (analysis && analysis.isBlocked);

  if (!isBlockedOrError) {
    return null;
  }

  return (
    <>
      {checkError && (
        <CheckResultPanel isError>
          <P2 style={{ margin: 0, color: RepositTheme.colors.negative, fontFamily: POPPINS_FONT }}>
            {typeof checkError === 'string' ? checkError : 'An error occurred while checking concurrent tenancy'}
          </P2>
        </CheckResultPanel>
      )}

      {analysis && analysis.message && checkResult && (
        <CheckResultPanel isWarning>
          <ResultMessage>{analysis.message}</ResultMessage>
          {checkResult.conflictingReposits && checkResult.conflictingReposits.length > 0 && (
            <ConflictList>
              {checkResult.conflictingReposits.map((conflict) => (
                <ConflictItem key={conflict.tenancyOrderId}>
                  <ConflictRow>
                    <ConflictLabel>Organization</ConflictLabel>
                    <ConflictValue>{conflict.organizationName}</ConflictValue>
                  </ConflictRow>
                  {conflict.address && (
                    <ConflictRow>
                      <ConflictLabel>Address</ConflictLabel>
                      <ConflictValue>{conflict.address}</ConflictValue>
                    </ConflictRow>
                  )}
                  {conflict.status && (
                    <ConflictRow>
                      <ConflictLabel>Status</ConflictLabel>
                      <ConflictValue>{CONFLICT_STATUS_LABELS[conflict.status]}</ConflictValue>
                    </ConflictRow>
                  )}
                  <ConflictRow>
                    <ConflictLabel>Tenancy Order ID</ConflictLabel>
                    <ConflictValue>
                      <IdentifierValue>{conflict.tenancyOrderId}</IdentifierValue>
                    </ConflictValue>
                  </ConflictRow>
                  <ConflictRow>
                    <ConflictLabel>Start Date</ConflictLabel>
                    <ConflictValue>{conflict.startDate}</ConflictValue>
                  </ConflictRow>
                  <ConflictRow>
                    <ConflictLabel>Days Apart</ConflictLabel>
                    <ConflictValue>{conflict.daysApart}</ConflictValue>
                  </ConflictRow>
                  {conflict.isSameOrganization && (
                    <ConflictRow>
                      <ConflictLabel />
                      <ConflictValue>
                        <em>Same organization</em>
                      </ConflictValue>
                    </ConflictRow>
                  )}
                </ConflictItem>
              ))}
            </ConflictList>
          )}
        </CheckResultPanel>
      )}
    </>
  );
};
